import { useEffect, useState, useCallback, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import AsyncSelect from 'react-select/async';
import Avatar from 'react-avatar';
import { fetchEmployeeDetails } from '../../../store/apis/employeeService';

import './UserAsyncAutocompleteSelect.scss';
import { ImageFileUtil } from '../../../utils';

function UserAsyncAutocompleteSelect({
  autoCompleteFn,
  mappedFn,
  onChange,
  value,
  inputIcon = null,
  nameAssessor = 'name',
  userCodeAssessor = 'code',
  designationAssessor = 'designation',
  imageAssessor = 'image',
  rightKeyAssessor = null,
  defaultShowMenu = false,
  defaultMenu = false,
  defaultInput = false,
  searchEmployee = false,
  customRightIcon = null,
  cursorType = 'text',
  ...props
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValueState, setInputValueState] = useState('');
  const debounceTimerRef = useRef(null);
  const DEBOUNCE_DELAY = 300; // milliseconds

  // Focus/blur now only toggle editing mode (we do NOT clear/restore the value)
  const handleFocus = () => {
    setIsEditing(true);
    setInputValueState('');
  };

  const handleBlur = () => {
    setIsEditing(false);
    setInputValueState('');
  };

  const fetchEmployeeDataMutation = useMutation({
    mutationFn: fetchEmployeeDetails,
    onSuccess: data => {
      if (data) {
        value.label = data?.[0]?.EmployeeName || 'Unknown';
      }
    },
    onError: error => {
      toast.error(error?.message || 'Failed to fetch employee data.', {
        className: 'toast-error',
      });
    },
  });

  useEffect(() => {
    if (searchEmployee) {
      value.label = '...';
      fetchEmployeeDataMutation.mutate({
        type: 'PROFILE',
        employeeId: value.value,
      });
    }
  }, []);

  const menuPortalTarget =
    typeof document !== 'undefined' ? document.body : null;

  const formatOptionLabel = (option, { context }) => {
    if (context === 'value' && !defaultInput) {
      return (
        <div className="d-flex align-items-center gap-1">
          <div className="d-flex align-items-center gap-1">
            {inputIcon && inputIcon}
            <span className="fw-semibold">{option.label}</span>{' '}
          </div>
          <span style={{ color: option.color || '#475467' }}>
            {rightKeyAssessor
              ? option?.[rightKeyAssessor]?.toUpperCase()
              : null}
          </span>
        </div>
      );
    }

    const getImage = () => {
      if (option?.[imageAssessor]) {
        return ImageFileUtil.getEmployeeFilePath(
          option?.[imageAssessor],
          option?.coCd,
        );
      }
      return null;
    };

    if (!defaultMenu) {
      return (
        <div className="d-flex align-items-center user-autocomplete-select">
          <div className="flex-grow-1 d-flex align-items-center gap-3">
            <Avatar
              name={option?.[nameAssessor] || 'Unknown'}
              round
              size="40px"
              color="#CFCBDC"
              fgColor="#475467"
              className="my-avatar"
              maxInitials={2}
              style={{
                fontSize: '14px',
                fontWeight: '600',
              }}
              src={getImage()}
            />
            <div className="d-flex flex-column">
              <span className="user-code">
                {option?.[userCodeAssessor]?.toUpperCase() || 'AE00000'}
              </span>
              <span>{option?.[nameAssessor] || '-'}</span>
            </div>
          </div>
          <span>{option?.[designationAssessor] || ''}</span>
        </div>
      );
    }

    return option?.label;
  };

  const tooltipText =
    value && !defaultInput
      ? `${value.label}${rightKeyAssessor ? ` ${value[rightKeyAssessor] ?? value.value}` : ''}`
      : props.tooltipTitle || 'Search...';

  const selectStyles = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    control: (base, state) => ({
      ...base,
      width: '100%',
      borderRadius: '8px',
      backgroundColor: state.isDisabled ? '#e9ecef' : '#fff',
      borderColor: state.isFocused ? '#86b7fe' : '#929398',
      boxShadow: state.isFocused
        ? '0 0 0 0.15rem rgba(13,110,253,.25)'
        : 'none',
      transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',
      '&:hover': { borderColor: state.isFocused ? '#86b7fe' : '#ced4da' },
      cursor: state.isDisabled ? 'not-allowed' : 'default',
    }),
    valueContainer: base => ({
      ...base,
      padding: '0.255rem 0.75rem',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? '#212529' : '#212529',
      paddingRight: '0.75rem',
      paddingLeft: 0,
      '&:hover': { color: '#212529' },
    }),
    menu: base => ({
      ...base,
      borderRadius: '0.375rem',
      overflow: 'hidden',
      boxShadow: '0 .5rem 1rem rgba(0,0,0,.15)',
      border: '1px solid #ced4da',
    }),
    placeholder: base => ({
      ...base,
      color: '#6c757d',
    }),
    singleValue: base => ({
      ...base,
      color: '#212529',
    }),
    container: base => ({
      ...base,
      cursor: cursorType,
      width: '100%',
    }),
  };

  // Custom Components for React Select
  const customComponents = {
    ...(customRightIcon && {
      DropdownIndicator: () => customRightIcon,
      IndicatorSeparator: () => null,
    }),
  };

  // Wrap onChange to exit editing immediately after a new selection
  const handleSelectChange = option => {
    onChange(option);
    setIsEditing(false);
    setInputValueState('');
  };

  // Create debounced load options function
  const debouncedLoadOptions = useCallback(
    inputValue =>
      new Promise(resolve => {
        // Clear previous timeout
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        // Set new timeout for debounced API call
        debounceTimerRef.current = setTimeout(async () => {
          if (!inputValue && !defaultShowMenu) {
            resolve([]);
            return;
          }
          const data = await autoCompleteFn(inputValue);
          const result = mappedFn ? mappedFn(data) : data;
          resolve(result);
        }, DEBOUNCE_DELAY);
      }),
    [autoCompleteFn, mappedFn, defaultShowMenu],
  );

  // Cleanup debounce timer on unmount
  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    [],
  );

  return (
    <span title={tooltipText} className="w-100">
      <AsyncSelect
        cacheOptions
        defaultOptions
        loadOptions={debouncedLoadOptions}
        onChange={handleSelectChange}
        className={`basic-single ${props.className || ''}`}
        classNamePrefix="select"
        isClearable={props.isClearable ?? true}
        isSearchable={props.isSearchable ?? true}
        placeholder={props.placeholder || 'Search...'}
        value={value}
        menuPortalTarget={menuPortalTarget}
        styles={selectStyles}
        formatOptionLabel={formatOptionLabel}
        isDisabled={props.isDisabled ?? false}
        components={{
          customComponents,
          DropdownIndicator: null,
          IndicatorSeparator: null,
          LoadingMessage: () => null,
          LoadingIndicator: () => null,
        }}
        noOptionsMessage={() => null}
        onFocus={handleFocus}
        onBlur={handleBlur}
        controlShouldRenderValue={!isEditing}
        inputValue={inputValueState}
        onInputChange={(val, { action }) => {
          if (
            action === 'input-change' ||
            action === 'set-value' ||
            action === 'menu-open'
          ) {
            setInputValueState(val ?? '');
          }
          return val;
        }}
        blurInputOnSelect
        closeMenuOnSelect
      />
    </span>
  );
}

export default UserAsyncAutocompleteSelect;
